import { dec } from '../quantity/decimal.js';
/**
 * Nutri-Score estimate for a recipe (docs/finished/ingredient-catalog-spec.md 6.10).
 *
 * The thresholds are data: a revised algorithm is a new table and a new version string.
 * This is the app's own estimate from a recipe's nutrition per 100 g – it is never shown as
 * an officially awarded label, and products are not scored with it (a package does not state
 * its fruit/vegetable share).
 */
export const NUTRI_SCORE_ALGORITHM_VERSION = '2023-general-1';
const range = (start, step, count) => Array.from({ length: count }, (_, i) => Math.round((start + i * step) * 1000) / 1000);
export const NUTRI_SCORE_TABLES = {
    GENERAL: {
        energyKj: range(335, 335, 10),
        sugars: [3.4, 6.8, 10, 14, 17, 20, 24, 27, 31, 34, 37, 41, 44, 48, 51],
        saturatedFat: range(1, 1, 10),
        salt: range(0.2, 0.2, 20),
        protein: [2.4, 4.8, 7.2, 9.6, 12, 14, 17],
        fibre: [3.0, 4.1, 5.2, 6.3, 7.4],
        fruitVeg: [
            { above: 40, points: 1 },
            { above: 60, points: 2 },
            { above: 80, points: 5 },
        ],
        grades: [0, 2, 10, 18],
    },
    BEVERAGE: {
        energyKj: [30, 90, 150, 210, 240, 270, 300, 330, 360, 390],
        sugars: [0.5, 2, 3.5, 5, 6, 7, 8, 9, 10, 11],
        saturatedFat: range(1, 1, 10),
        salt: range(0.2, 0.2, 20),
        protein: [1.2, 1.5, 1.8, 2.1, 2.4, 2.7, 3.0],
        fibre: [3.0, 4.1, 5.2, 6.3, 7.4],
        fruitVeg: [
            { above: 40, points: 2 },
            { above: 60, points: 4 },
            { above: 80, points: 6 },
        ],
        // Only water is A; a computed beverage never gets better than B.
        grades: [-Infinity, 2, 6, 9],
    },
};
function points(value, thresholds) {
    let earned = 0;
    for (const t of thresholds)
        if (value.gt(t))
            earned++;
    return earned;
}
export function computeNutriScore(input, category = 'GENERAL') {
    const t = NUTRI_SCORE_TABLES[category];
    const negative = points(dec(input.energyKj), t.energyKj) +
        points(dec(input.sugars), t.sugars) +
        points(dec(input.saturatedFat), t.saturatedFat) +
        points(dec(input.salt), t.salt);
    const protein = points(dec(input.protein), t.protein);
    const fibre = points(dec(input.fibre), t.fibre);
    const share = Math.max(0, Math.min(100, input.fruitVegLegumePercent));
    let fruitVeg = 0;
    for (const step of t.fruitVeg)
        if (share > step.above)
            fruitVeg = step.points;
    // With 11 or more negative points protein no longer offsets them (general foods).
    const countProtein = category === 'BEVERAGE' || negative < 11;
    const positive = fibre + fruitVeg + (countProtein ? protein : 0);
    const score = negative - positive;
    const [a, b, c, d] = t.grades;
    const letter = score <= a ? 'A' : score <= b ? 'B' : score <= c ? 'C' : score <= d ? 'D' : 'E';
    return {
        letter,
        score,
        negativePoints: negative,
        positivePoints: positive,
        algorithmVersion: NUTRI_SCORE_ALGORITHM_VERSION,
    };
}
export function isNutriScoreLetter(value) {
    return value === 'A' || value === 'B' || value === 'C' || value === 'D' || value === 'E';
}
//# sourceMappingURL=nutriscore.js.map